import { styled, keyframes } from '@mui/material/styles';
import { Box, Typography, Button, TableCell, TableRow, Chip, Checkbox, Pagination as MuiPagination, Paper, ToggleButton as MuiToggleButton, ToggleButtonGroup as MuiToggleButtonGroup } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

export const ModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 700,
  height: '90%',
  backgroundColor: '#ffffff',
  borderRadius: 8,
  boxShadow: 24,
  overflow: 'hidden',
  outline: 'none',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    height: '100%',
    borderRadius: 0
  }
}));

export const Header = styled('div')({
  boxShadow: '0px 1px 12px rgb(0 0 0 / 11%),' +
    '0px 2px 14px rgb(0 0 0 / 5%), 0px 1px 18px rgb(0 0 0 / 8%)',
  position: 'relative'
});

export const HeaderTitle = styled('div')(({ theme }) => ({
  padding: theme.spacing(3, 4, 0, 4),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2, 2, 0)
  }
}));

export const Title = styled(Typography)({
  fontWeight: 500
});

export const SubTitle = styled(Typography)({
  minHeight: 24
});

export const HeaderActions = styled('div')(({ theme }) => ({
  position: 'sticky',
  display: 'grid',
  gridTemplateColumns: '216px 163px 190px',
  gridTemplateAreas: '"switch filter addBtn"',
  gridGap: theme.spacing(3),
  padding: theme.spacing(2, 4, 2, 4),
  width: '100%',
  zIndex: 1100,
  [theme.breakpoints.down('sm')]: {
    gridGap: theme.spacing(1.25),
    padding: theme.spacing(2),
    gridTemplateAreas: '"switch addBtn" "filter filter"',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: 'auto auto'
  }
}));

export const ToggleButtonGroup = styled(MuiToggleButtonGroup)({
  boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.16),' +
    '0px 2px 4px rgba(0, 0, 0, 0.12), 0px 1px 8px rgba(0, 0, 0, 0.1)',
  '& > :last-child': {
    marginLeft: 0
  }
});

export const ToggleButton = styled(MuiToggleButton)({
  width: '50%',
  color: '#000',
  textTransform: 'capitalize',
  fontSize: '1rem',
  fontWeight: 400,
  backgroundColor: '#EEE',
  '&.Mui-selected': {
    color: '#fff',
    backgroundColor: '#5eb676',
    '&:hover': {
      backgroundColor: '#5eb676'
    }
  }
});

export const Content = styled('div', {
  shouldForwardProp: prop => prop !== 'showChangesLabel'
})(({ theme, showChangesLabel }) => ({
  height: showChangesLabel ? 'calc(100% - 253px)' : 'calc(100% - 227px)',
  position: 'relative',
  [theme.breakpoints.down('sm')]: {
    height: 'calc(100% - 240px)'
  }
}));

export const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3, 4, 2, 3.5),
  boxShadow: 'none',
  maxHeight: '100%',
  overflowY: 'auto',
  '&::-webkit-scrollbar-track': {
    WebkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.3)',
    borderRadius: 10,
    backgroundColor: '#F5F5F5'
  },
  '&::-webkit-scrollbar': {
    width: 8,
    backgroundColor: '#F5F5F5'
  },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: 10,
    WebkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,.3)',
    backgroundColor: '#a0a0a0'
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1, 1.25)
  },
  borderRadius: 0
}));

export const StyledCloseIcon = styled(CloseIcon)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  right: theme.spacing(2),
  zIndex: 1,
  '&:hover': {
    cursor: 'pointer'
  }
}));

const blink = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0.2;
  }
`;

export const StyledTable = styled('table', {
  shouldForwardProp: prop => prop !== 'loading'
})(({ theme, loading }) => ({
  display: 'table',
  width: '100%',
  borderCollapse: 'collapse',
  borderSpacing: 0,
  '& thead th:first-of-type': {
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(0, 1.5)
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0, 0.75)
    }
  },
  '& tbody td:first-of-type': {
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(0, 1.5)
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0, 0.75)
    }
  },
  ...(loading && {
    '& tbody tr': {
      filter: 'blur(5px)',
      pointerEvents: 'none'
    },
    '& tbody th, & tbody td': {
      animation: `${blink} 1.5s alternate infinite linear`
    }
  })
}));

export const StyledHeaderRow = styled(TableRow)({
  '& th': {
    border: 0,
    padding: '6px 16px'
  },
  '& th[class*="MuiTableCell-paddingCheckbox"]': {
    padding: '0px 12px 0px 16px'
  }
});

export const StyledTableRow = styled(TableRow, {
  shouldForwardProp: prop => prop !== 'disabled' && prop !== 'withoutHover'
})(({ theme, disabled, withoutHover }) => ({
  color: 'rgba(0, 0, 0, 0.87)',
  '&:hover': {
    backgroundColor: withoutHover ? 'transparent !important' : theme.palette.action.hover,
    cursor: withoutHover ? 'default' : 'pointer'
  },
  ...(disabled && {
    pointerEvents: 'none',
    opacity: 0.5
  }),
  '& th': {
    border: 0
  }
}));

export const StyledTableCell = styled(TableCell, {
  shouldForwardProp: prop => prop !== 'withoutHover'
})(({ theme, padding, withoutHover }) => ({
  border: 0,
  padding: '6px 16px',
  ...(padding === 'checkbox' && {
    width: 24,
    verticalAlign: 'middle',
    textAlign: 'center'
  }),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.75, 0, 0.75, 1.5)
  },
  '&:last-child': {
    [theme.breakpoints.down('sm')]: {
      paddingRight: 0
    }
  },
  '&:hover': {
    cursor: withoutHover ? 'default' : 'pointer',
    ...(withoutHover && {
      backgroundColor: 'transparent !important'
    })
  },
  '&[scope="row"]': {
    height: 44
  }
}));

export const TableCellType = styled(TableCell, {
  shouldForwardProp: prop => prop !== 'withoutHover'
})(({ withoutHover }) => ({
  cursor: 'default',
  color: '#828282',
  '&:hover': {
    cursor: 'default',
    ...(withoutHover && {
      backgroundColor: 'transparent !important'
    })
  }
}));

export const CheckboxCell = styled(TableCell)(({ padding }) => ({
  verticalAlign: 'top',
  border: 0,
  ...(padding === 'checkbox' && {
    width: 24,
    verticalAlign: 'middle',
    textAlign: 'center'
  })
}));

export const TitleCellContainer = styled('div', {
  shouldForwardProp: prop => prop !== 'disabledBy'
})(({ disabledBy }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  ...(disabledBy && {
    position: 'relative',
    marginBottom: 12
  })
}));

export const DisabledByText = styled('span')({
  position: 'absolute',
  left: 0,
  bottom: -18,
  maxWidth: '90%',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  fontSize: 12,
  fontWeight: 200
});

export const TableCheckbox = styled(Checkbox)(({ theme }) => ({
  padding: 0,
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(0, 1.5)
  }
}));

export const TableAlertCheckbox = styled(Checkbox)({
  color: '#0000008a',
  marginRight: 6,
  '&.Mui-checked': {
    color: '#0000008a'
  }
});

export const UncheckedAlertCheckbox = styled('span')({
  color: 'rgba(0, 0, 0, 0.18)',
  display: 'inline-block'
});

export const BodyCheckbox = styled(Checkbox)({
  padding: 0
});

export const StyledChip = styled(Chip)({
  border: 0
});

export const PaginationContainer = styled('div')(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  width: '100%',
  height: 48,
  backgroundColor: '#fff',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  boxShadow: '0px 1px 12px rgb(0 0 0 / 11%),' +
    '0px 2px 14px rgb(0 0 0 / 5%), 0px 1px 18px rgb(0 0 0 / 8%)',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    position: 'absolute',
    bottom: 0,
    left: 0
  }
}));

export const StyledPagination = styled(MuiPagination)(({ theme }) => ({
  padding: theme.spacing(1, 0, 1, 0),
  '& > ul': {
    justifyContent: 'center'
  }
}));

export const PaginationCount = styled('div')(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(2),
  color: '#828282',
  [theme.breakpoints.down('sm')]: {
    display: 'none'
  }
}));

const ActionButton = styled(Button)(({ theme }) => ({
  position: 'fixed',
  right: 60,
  bottom: 95,
  padding: theme.spacing(1.75, 3),
  color: '#fff',
  boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.16),' +
  '0px 2px 4px rgba(0, 0, 0, 0.12),' +
  '0px 1px 8px rgba(0, 0, 0, 0.1)',
  borderRadius: 58
}));

export const RemoveButton = styled(ActionButton)({
  backgroundColor: '#EB5757',
  '&:hover': {
    backgroundColor: '#EB5757'
  }
});

export const AddDevicesButton = styled(ActionButton)({
  backgroundColor: '#74B47D',
  '&:hover': {
    backgroundColor: '#74B47D'
  }
});

export const LinkContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  textTransform: 'uppercase'
});

export const StyledLink = styled('a')({
  color: '#212121',
  textDecoration: 'none',
  textTransform: 'uppercase',
  borderBottom: '1px #212121 solid',
  paddingBottom: 1,
  marginTop: 4,
  '&:hover': {
    borderBottom: 'none'
  }
});

export const AddButtonLabel = styled('span')({
  paddingTop: 2
});

export const BottomInfoMessage = styled('div')({
  display: 'block',
  position: 'absolute',
  width: '100%',
  backgroundColor: '#FACE31',
  padding: '4px 8px',
  fontWeight: 500,
  bottom: -28,
  left: 0
});
